import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "@/utils/utils"

// FIX: lock/unlock now tracks the Root's actual `open` boolean directly,
// instead of DialogContent's mount/unmount. Radix delays DialogContent's
// real unmount until its exit animation finishes (Presence) — if that
// animationend event is ever missed (rapid open/close, backgrounded tab,
// an interrupted transition), the old mount-effect-based lock could
// increment without a matching decrement, permanently pinning body scroll.
// Tying the lock to `open` itself is immune to animation-timing edge cases.
const Dialog = ({ open, ...props }) => {
  React.useEffect(() => {
    if (open) {
      lockScroll()
      return () => unlockScroll()
    }
  }, [open])
  return <DialogPrimitive.Root open={open} {...props} />
}
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

// Track how many dialogs are open so nested dialogs don't unlock too early.
// Exported as the single shared body-scroll-lock implementation — Layout.jsx's
// ProfileModal now imports and uses these instead of its own separate copy,
// so there is only ever one source of truth for body.style + openCount.
let openCount = 0
let savedScrollY = 0

// On mobile, html/body are already a fixed, non-scrolling shell (see
// index.css [data-scroll-shell="true"]) and Layout.jsx's <main> is the
// real scroll container instead — so freezing background scroll there
// means freezing <main>, not body. Tablet still scrolls via the document,
// so it keeps the original body-lock behavior unchanged.
const usesMainScroll = () => document.documentElement.getAttribute('data-scroll-shell') === 'true'

export const lockScroll = () => {
  if (openCount === 0) {
    const mainEl = usesMainScroll() ? document.querySelector('main') : null
    if (mainEl) {
      savedScrollY = mainEl.scrollTop
      mainEl.style.overflowY = 'hidden'
    } else {
      savedScrollY = window.scrollY
      document.documentElement.classList.add('dialog-open')
      document.body.classList.add('dialog-open')
      document.body.style.position = 'fixed'
      document.body.style.top = `-${savedScrollY}px`
      document.body.style.left = '0'
      document.body.style.right = '0'
      document.body.style.width = '100%'
      document.body.style.overflowY = 'scroll'
    }
  }
  openCount++
  // FIX: keep pull-to-refresh suppressed while any Radix dialog is open
  window.__dialogOpenCount = (window.__dialogOpenCount || 0) + 1
}

export const unlockScroll = () => {
  openCount = Math.max(0, openCount - 1)
  // FIX: decrement global counter so pull-to-refresh re-enables correctly
  window.__dialogOpenCount = Math.max(0, (window.__dialogOpenCount || 1) - 1)
  if (openCount === 0) {
    const mainEl = usesMainScroll() ? document.querySelector('main') : null
    if (mainEl) {
      mainEl.style.overflowY = 'auto'
      mainEl.scrollTop = savedScrollY
    } else {
      document.documentElement.classList.remove('dialog-open')
      document.body.classList.remove('dialog-open')
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.left = ''
      document.body.style.right = ''
      document.body.style.width = ''
      document.body.style.overflowY = ''
      window.scrollTo(0, savedScrollY)
    }
  }
}

// FIX: safety net for the whole class of "React never got to run its
// DialogContent unmount cleanup" bugs — OS app-handoff intents (sms:/tel:/
// mailto:), the installed WebAPK getting suspended mid-gesture, or anything
// else that can pause this tab before lockScroll's owning dialog unmounts.
// Instead of patching every individual call site that might hand off to the
// OS, force-clear the lock the instant the page is hidden for ANY reason,
// and only re-apply it if a dialog is still legitimately open (openCount > 0)
// once the page becomes visible again. A leaked lock can then never survive
// past the next visibility change, app-wide, regardless of what caused React
// to miss its cleanup.
let hiddenWithLockActive = false
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (openCount > 0) {
        const mainEl = usesMainScroll() ? document.querySelector('main') : null
        if (mainEl) {
          mainEl.style.overflowY = 'auto'
        } else {
          document.documentElement.classList.remove('dialog-open')
          document.body.classList.remove('dialog-open')
          document.body.style.position = ''
          document.body.style.top = ''
          document.body.style.left = ''
          document.body.style.right = ''
          document.body.style.width = ''
          document.body.style.overflowY = ''
        }
        hiddenWithLockActive = true
      }
    } else if (document.visibilityState === 'visible') {
      if (hiddenWithLockActive && openCount > 0) {
        const mainEl = usesMainScroll() ? document.querySelector('main') : null
        if (mainEl) {
          mainEl.style.overflowY = 'hidden'
        } else {
          document.documentElement.classList.add('dialog-open')
          document.body.classList.add('dialog-open')
          document.body.style.position = 'fixed'
          document.body.style.top = `-${savedScrollY}px`
          document.body.style.left = '0'
          document.body.style.right = '0'
          document.body.style.width = '100%'
          document.body.style.overflowY = 'scroll'
        }
      }
      hiddenWithLockActive = false
    }
  })
}

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80",
      "data-[state=open]:animate-in data-[state=closed]:animate-out",
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed z-50",
          // Horizontally centered
          "left-[50%] translate-x-[-50%]",
          // Width
          "w-[calc(100vw-2rem)] max-w-lg",
          // Appearance
          "gap-2 border bg-background p-8 shadow-lg rounded-xl",
          // Animations — fade + zoom only
          "duration-200",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className
        )}
        style={{
          // Top edge aligned with bottom of the fixed header (h-12 = 48px)
          top: '55px',
          // 100dvh = dynamic viewport height — shrinks automatically when the
          // on-screen keyboard appears, so the dialog bottom always sits at
          // exactly the top edge of the keyboard. No JS needed.
          maxHeight: 'calc(100dvh - 55px)',
          // Content inside scrolls if it overflows
          overflowY: 'auto',
        }}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-5 top-5 rounded-xl opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col space-y-1 text-center sm:text-left", className)}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}